import type { ElementRef } from '@angular/core'
import { afterNextRender, Component, computed, inject, viewChild } from '@angular/core'
import { NgOptimizedImage } from '@angular/common'
import { CdkTrapFocus } from '@angular/cdk/a11y'
import { ButtonComponent } from '../../../shared/components/button/button.component'
import { CriticalUpdateService } from '../../../shared/services/critical-update-service/critical-update.service'

/** Covers the app while a critical live update installs, and offers a retry when it fails. */
@Component({
  selector: 'aw-critical-update-screen',
  templateUrl: './critical-update-screen.component.html',
  styleUrl: './critical-update-screen.component.scss',
  imports: [NgOptimizedImage, ButtonComponent, CdkTrapFocus]
})
export class CriticalUpdateScreenComponent {
  private readonly criticalUpdateService = inject(CriticalUpdateService)
  private readonly dialog = viewChild.required<ElementRef<HTMLElement>>('dialog')

  protected readonly translations = q.translations.CriticalUpdateScreenComponent
  protected readonly isFailed = computed(() => this.criticalUpdateService.state().status === 'failed')
  protected readonly progress = computed(() => this.getProgress())
  // Without a width binding, the waiting animation in the stylesheet sets the width.
  protected readonly fillWidth = computed(() => (this.progress() > 0 ? this.progress() : null))
  protected readonly title = computed(() => (this.isFailed() ? this.translations.failedTitle : this.translations.title))
  protected readonly description = computed(() =>
    this.isFailed() ? this.translations.failedDescription : this.translations.description
  )

  constructor() {
    // Move screen reader focus into the dialog, because the app below is no longer usable.
    afterNextRender(() => this.dialog().nativeElement.focus())
  }

  /**
   * Returns the rounded download progress in percent.
   * @withSignal criticalUpdateService.state
   * @returns {number}
   */
  private getProgress(): number {
    const state = this.criticalUpdateService.state()
    return state.status === 'installing' ? Math.round(state.progress) : 0
  }

  /** Starts the install again. */
  protected retry(): void {
    void this.criticalUpdateService.retry()
  }
}
